import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller, useWatch, useFieldArray } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CreatableSelect from "react-select/creatable";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import moment from "moment";
import Select, { components } from "react-select";
import { getAllQuantityTypes, deleteQuantityType } from "../../components/common/globalapi/Api";
import CreateQuantityType from "./CreateQuantityType";

// Custom Option to render Edit and Delete buttons inline
const CustomOption = (props) => {
  return (
    <components.Option {...props}>
      <div className="d-flex justify-content-between align-items-center w-100">
        <span className="text-truncate">{props.data.label}</span>
        {props.data.id && (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-primary"
              onClick={(e) => {
                e.stopPropagation();
                if (props.selectProps.onEditItem) props.selectProps.onEditItem(props.data);
              }}
              title="Edit"
            >
              <i className="bi bi-pencil"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-danger"
              onClick={(e) => {
                e.stopPropagation();
                if (props.selectProps.onDeleteItem) props.selectProps.onDeleteItem(props.data);
              }}
              title="Delete"
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        )}
      </div>
    </components.Option>
  );
};

const AddProduct = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      addProduct: [
        {
          registerId: "",
          vendorName: "",
          vendorAddress: "",
          vendorPhone: "",
          productName: "",
          hsnCode: "",
          category: "",
          productType: "",
          productFor: "Both",
          purchasePrice: "",
          highLevelCustomerPrice: "",
          midLevelCustomerPrice: "",
          lowLevelCustomerPrice: "",
          manufacturingDate: null,
          expiryDate: null,
          stockStatus: "In Stock",
          quantity: "",
          quantityType: "",
          otherQuantityType: "",
          productImage: null,
        },
      ],
      category: "",
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "addProduct",
  });

  const { quantityType, addProduct } = watch();

  const userId = sessionStorage.getItem("userId");
  const fileInputRefs = useRef({});
  const addProductValues = watch("addProduct");
  const companyDetails = (() => {
    try {
      return JSON.parse(localStorage.getItem("companyDetails") || "{}");
    } catch (e) {
      return {};
    }
  })();
  const queryClient = useQueryClient();

  // Fetch product categories for current userId
  const {
    data: categoriesData = [],
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ["productCategories", userId],
    queryFn: async () => {
      if (!userId) return [];
      const resp = await axios.get(`${process.env.REACT_APP_HOST}/api/Products/categories/${userId}`);
      return resp.data || [];
    },
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });

  // Fetch Quantity Types
  const {
    data: quantityTypesData = [],
    isLoading: quantityTypesLoading,
  } = useQuery({
    queryKey: ["quantityTypes", userId],
    queryFn: async () => {
      if (!userId) return [];
      const resp = await getAllQuantityTypes(userId);
      return resp.data || [];
    },
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });

  const [editingQuantityType, setEditingQuantityType] = useState(null);

  // DELETE API for Quantity Type
  const deleteQuantityMutation = useMutation({
    mutationFn: async (id) => {
      const response = await deleteQuantityType(userId, id);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Quantity Type deleted successfully!");
      queryClient.invalidateQueries(["quantityTypes", userId]);
    },
    onError: (error) => {
      toast.error(error?.response?.data || "Failed to delete Quantity Type.");
    }
  });

  const handleDeleteQuantityType = (data) => {
    if (window.confirm(`Are you sure you want to delete '${data.label}'?`)) {
      deleteQuantityMutation.mutate(data.id);
    }
  };

  const handleEditQuantityType = (data) => {
    setEditingQuantityType(data.originalData);
    // Programmatically open the modal since we intercepted the click
    const modalBtn = document.getElementById("hiddenCreateQtyModalBtn");
    if (modalBtn) modalBtn.click();
  };

  // POST API START
  const { mutate, isPending: postLoading } = useMutation({
    mutationFn: async (postData) => {
      const response = await axios.post(
        `${process.env.REACT_APP_HOST}/api/Products/addproduct`,
        postData
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Product Added Successfully!");
      setTimeout(() => navigate("/viewProduct"), 2000);
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        toast.error(error?.response?.data || "Product already exists.");
      } else {
        toast.error("Failed to add Product. Please try again.");
      }
    },
  });

  // UPDATE API
  const { mutate: updateApi, isPending: putLoading } = useMutation({
    mutationFn: async (postData) => {
      const response = await axios.put(
        `${process.env.REACT_APP_HOST}/api/Products/editProduct/${state?.id}`,
        postData
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Product Updated Successfully!");
      setTimeout(() => navigate("/viewProduct"), 2000);
    },
    onError: (error) => {
      toast.error("Failed to update the Product. Please try again.");
    },
  });


  useEffect(() => {
    if (state?.id) {
      setValue("addProduct.0.registerId", state?.registerId || "");
      setValue("addProduct.0.vendorName", state?.vendorName || "");
      setValue("addProduct.0.vendorAddress", state?.vendorAddress || "");
      setValue("addProduct.0.vendorPhone", state?.vendorPhone || "");
      setValue("addProduct.0.productName", state?.productName || "");
      setValue("addProduct.0.hsnCode", state?.hsnCode || "");
      setValue("addProduct.0.category", state?.category || "");
      setValue("addProduct.0.productType", state?.productType || "");
      setValue("addProduct.0.productFor", state?.productFor || "Both");
      setValue("addProduct.0.purchasePrice", state?.purchasePrice || "");
      setValue("addProduct.0.highLevelCustomerPrice", state?.highLevelCustomerPrice || "");
      setValue("addProduct.0.midLevelCustomerPrice", state?.midLevelCustomerPrice || "");
      setValue("addProduct.0.lowLevelCustomerPrice", state?.lowLevelCustomerPrice || "");
      setValue("addProduct.0.stockStatus", state?.stockStatus || "In Stock");
      setValue("addProduct.0.quantity", state?.quantity || "");
      setValue("addProduct.0.quantityType", state?.quantityType || "");
      setValue("addProduct.0.otherQuantityType", state?.otherQuantityType || "");
      setValue(
        "addProduct.0.manufacturingDate",
        state?.manufacturingDate
          ? moment(state?.manufacturingDate).format("YYYY-MM-DD")
          : null
      );
      setValue(
        "addProduct.0.expiryDate",
        state?.expiryDate ? moment(state?.expiryDate).format("YYYY-MM-DD") : null
      );
      // store productImage without data URL prefix
      const imgFromState = state?.productImage
        ? state.productImage.replace(/^data:.*;base64,/, "")
        : null;
      setValue("addProduct.0.productImage", imgFromState);
      // populate global category field if provided in state
      setValue("category", state?.category || "");
    }
    else {
      // if no edit state, populate registerId from logged-in company `id` and make it read-only
      const reg = companyDetails?.id || "";
      if (reg) setValue("addProduct.0.registerId", reg);
    }
  }, [state, setValue]);

  // convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (index, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setValue(`addProduct.${index}.productImage`, null);
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      // strip data URL prefix (e.g. data:image/jpeg;base64,)
      const rawBase64 = base64.replace(/^data:.*;base64,/, "");
      setValue(`addProduct.${index}.productImage`, rawBase64);
    } catch (err) {
      console.error("Failed to convert file to base64", err);
      setValue(`addProduct.${index}.productImage`, null);
    }
  };

  // Watch quantity value
  const quantity = useWatch({ control, name: "addProduct.0.quantity" });

  useEffect(() => {
    if (quantity === 0 || quantity === "0" || quantity === "") {
      setValue("addProduct.0.stockStatus", "Out Of Stock");
    } else {
      setValue("addProduct.0.stockStatus", "In Stock");
    }
  }, [quantity, setValue]);

  // FORM DATA
  const onSubmit = (data) => {
    const formDataArray = data?.addProduct || [];

    if (state?.id) {
      // Submit a single product (first item in array)
      const firstProduct = formDataArray[0];
      const editData = {
        registerId: userId || firstProduct?.registerId || companyDetails?.id || "",
        vendorName: firstProduct?.vendorName || "",
        vendorAddress: firstProduct?.vendorAddress || "",
        vendorPhone: firstProduct?.vendorPhone || "",
        productName: firstProduct?.productName || "",
        productImage: firstProduct?.productImage || null,
        hsnCode: firstProduct?.hsnCode || "",
        category: data?.category || firstProduct?.category || "",
        productType: firstProduct?.productType || "",
        productFor: firstProduct?.productFor || "",
        purchasePrice: firstProduct?.purchasePrice ? Number(firstProduct.purchasePrice) : firstProduct?.purchasePrice,
        highLevelCustomerPrice: firstProduct?.highLevelCustomerPrice ? Number(firstProduct.highLevelCustomerPrice) : firstProduct?.highLevelCustomerPrice,
        midLevelCustomerPrice: firstProduct?.midLevelCustomerPrice ? Number(firstProduct.midLevelCustomerPrice) : firstProduct?.midLevelCustomerPrice,
        lowLevelCustomerPrice: firstProduct?.lowLevelCustomerPrice ? Number(firstProduct.lowLevelCustomerPrice) : firstProduct?.lowLevelCustomerPrice,
        manufacturingDate: firstProduct?.manufacturingDate ? moment(firstProduct.manufacturingDate).format("YYYY-MM-DD") : null,
        expiryDate: firstProduct?.expiryDate ? moment(firstProduct.expiryDate).format("YYYY-MM-DD") : null,
        stockStatus: firstProduct?.stockStatus || "",
        quantity: firstProduct?.quantity ? Number(firstProduct.quantity) : firstProduct?.quantity,
        quantityType: firstProduct?.quantityType || "",
      };
      updateApi(editData); // single item update
    } else {
      // Send array of objects for batch addition
      const payload = formDataArray.map((item) => ({
        registerId: userId || item?.registerId || companyDetails?.id || "",
        vendorName: item?.vendorName || "",
        vendorAddress: item?.vendorAddress || "",
        vendorPhone: item?.vendorPhone || "",
        productName: item?.productName || "",
        productImage: item?.productImage || null,
        hsnCode: item?.hsnCode || "",
        category: data?.category || item?.category || "",
        productType: item?.productType || "",
        productFor: item?.productFor || "",
        purchasePrice: item?.purchasePrice ? Number(item.purchasePrice) : item?.purchasePrice,
        highLevelCustomerPrice: item?.highLevelCustomerPrice ? Number(item.highLevelCustomerPrice) : item?.highLevelCustomerPrice,
        midLevelCustomerPrice: item?.midLevelCustomerPrice ? Number(item.midLevelCustomerPrice) : item?.midLevelCustomerPrice,
        lowLevelCustomerPrice: item?.lowLevelCustomerPrice ? Number(item.lowLevelCustomerPrice) : item?.lowLevelCustomerPrice,
        manufacturingDate: item?.manufacturingDate ? moment(item.manufacturingDate).format("YYYY-MM-DD") : null,
        expiryDate: item?.expiryDate ? moment(item.expiryDate).format("YYYY-MM-DD") : null,
        stockStatus: item?.stockStatus || "",
        quantity: item?.quantity ? Number(item.quantity) : item?.quantity,
        quantityType: item?.quantityType || "",
      }));
      mutate(payload); // submit array
    }
  };

  return (
    <div className="container-fluid py-4 min-vh-100 bg-light">
      <div className="row justify-content-center">
        <div className="col-12 col-xxl-11">
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">

            <div className="card-body p-0">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-4" style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label small fw-bold text-secondary">Category <span className="text-danger">*</span></label>
                      <Controller
                        name="category"
                        control={control}
                        rules={{ required: 'Category is required' }}
                        render={({ field, fieldState: { error } }) => {
                          const options = (Array.isArray(categoriesData) ? categoriesData : []).map((c) => ({ label: c, value: c }));
                          return (
                            <>
                              <CreatableSelect
                                isClearable
                                isLoading={categoriesLoading}
                                onChange={(selected) => field.onChange(selected ? selected.value : '')}
                                onCreateOption={(inputValue) => {
                                  field.onChange(inputValue);
                                  queryClient.invalidateQueries(["productCategories", userId]);
                                }}
                                options={options}
                                value={field.value ? { label: field.value, value: field.value } : null}
                                placeholder="Select or type category"
                                classNamePrefix="react-select"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    minHeight: 40,
                                    height: 40,
                                    borderRadius: 6,
                                  }),
                                  valueContainer: (provided) => ({
                                    ...provided,
                                    padding: '4px 8px',
                                    height: 40,
                                  }),
                                  input: (provided) => ({
                                    ...provided,
                                    margin: 0,
                                    padding: 0,
                                  }),
                                }}
                              />
                              {error && <div className="text-danger small mt-1">{error.message}</div>}
                            </>
                          );
                        }}
                      />
                    </div>
                  </div>

                  {fields?.map((field, index) => {
                    return (
                      <div key={field?.id} className="card mb-4 border-0 shadow-sm rounded-3 bg-white border">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3 border-bottom-0">
                          <span className="badge bg-primary rounded-pill">Product #{index + 1}</span>
                          {index > 0 && !state?.id && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="btn btn-link text-danger p-0 text-decoration-none small fw-bold"
                            >
                              <i className="bi bi-trash me-1"></i> Remove Item
                            </button>
                          )}
                        </div>

                        <div className="card-body p-4">
                          {/* Vendor Information Section */}
                          <div className="row g-3 mb-4">
                            <div className="col-12 mb-2">
                              <h6 className="text-primary fw-bold border-bottom pb-2">
                                <i className="bi bi-person-badge me-2"></i>Vendor Information
                              </h6>
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">Register ID</label>
                              <Controller
                                name={`addProduct.${index}.registerId`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    value={addProductValues && addProductValues[index] ? addProductValues[index].registerId || field.value || "" : field.value || ""}
                                    type="text"
                                    placeholder="Enter Register ID"
                                    className="form-control form-control-sm shadow-sm border-dark"
                                    disabled
                                  />
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">Vendor Name</label>
                              <Controller
                                name={`addProduct.${index}.vendorName`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="text"
                                    placeholder="Enter Vendor Name"
                                    className="form-control form-control-sm shadow-sm border-dark"
                                  />
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">Vendor Phone</label>
                              <Controller
                                name={`addProduct.${index}.vendorPhone`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="text"
                                    placeholder="Enter Phone Number"
                                    className="form-control form-control-sm shadow-sm border-dark"
                                  />
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">Vendor Address</label>
                              <Controller
                                name={`addProduct.${index}.vendorAddress`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="text"
                                    placeholder="Enter Address"
                                    className="form-control form-control-sm shadow-sm border-dark"
                                  />
                                )}
                              />
                            </div>
                          </div>

                          {/* Product Details Section */}
                          <div className="row g-3 mb-4">
                            <div className="col-12 mb-2">
                              <h6 className="text-primary fw-bold border-bottom pb-2">
                                <i className="bi bi-box-seam me-2"></i>Product Details
                              </h6>
                            </div>

                            <div className="col-12 col-md-6 col-lg-4">
                              <label className="form-label small fw-bold text-secondary">
                                Product Name <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name={`addProduct.${index}.productName`}
                                control={control}
                                rules={{ required: "Product Name is required" }}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <input
                                      {...field}
                                      type="text"
                                      placeholder="Add Product"
                                      className={`form-control form-control-sm shadow-sm border-dark increased-input ${error ? 'is-invalid' : ''}`}
                                    />
                                    {error && <div className="invalid-feedback">{error.message}</div>}
                                  </>
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">HSN Code</label>
                              <Controller
                                name={`addProduct.${index}.hsnCode`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="text"
                                    placeholder="Code"
                                    className="form-control form-control-sm shadow-sm border-dark increased-input"
                                  />
                                )}
                              />
                            </div>

                            {/* Category moved to top-level; per-row category removed */}

                            {/* <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary">Product Type</label>
                              <Controller
                                name={`addProduct.${index}.productType`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="text"
                                    placeholder="Enter Product Type"
                                    className="form-control form-control-sm shadow-sm border-dark increased-input"
                                  />
                                )}
                              />
                            </div> */}

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary">Product For</label>
                              <Controller
                                name={`addProduct.${index}.productFor`}
                                control={control}
                                render={({ field }) => (
                                  <select {...field} className="form-select form-select-sm shadow-sm border-dark">
                                    <option value="">Product For</option>
                                    <option value="Sales">Sales</option>
                                    <option value="Purchase">Purchase</option>
                                    <option value="Both">Both</option>
                                  </select>
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary">Product Image</label>
                              <input
                                ref={(el) => (fileInputRefs.current[index] = el)}
                                type="file"
                                accept="image/*"
                                className="form-control form-control-sm shadow-sm border-dark increased-input"
                                onChange={(e) => handleFileChange(index, e)}
                              />

                              {addProductValues && addProductValues[index] && addProductValues[index].productImage ? (
                                <div className="d-flex align-items-center mt-2">
                                  <img
                                    src={`data:image/*;base64,${addProductValues[index].productImage}`}
                                    alt="preview"
                                    style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, marginRight: 8 }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => {
                                      // clear form value and file input
                                      setValue(`addProduct.${index}.productImage`, null);
                                      const inp = fileInputRefs.current[index];
                                      if (inp) inp.value = "";
                                    }}
                                    title="Remove Image"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>

                          {/* Pricing & Dates Section */}
                          <div className="row g-3 mb-4">
                            <div className="col-12 mb-2">
                              <h6 className="text-primary fw-bold border-bottom pb-2">
                                ₹ Pricing & Dates
                              </h6>
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary">
                                Purchase Price <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name={`addProduct.${index}.purchasePrice`}
                                control={control}
                                rules={{ required: "Required" }}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <input
                                      {...field}
                                      type="number"
                                      placeholder="0.00"
                                      className={`form-control form-control-sm shadow-sm border-dark increased-input ${error ? 'is-invalid' : ''}`}
                                    />
                                    {error && <div className="invalid-feedback">{error.message}</div>}
                                  </>
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary">MRP</label>
                              <Controller
                                name={`addProduct.${index}.highLevelCustomerPrice`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="number"
                                    placeholder="0.00"
                                    className="form-control form-control-sm shadow-sm border-dark increased-input"
                                  />
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary">Retailer Price</label>
                              <Controller
                                name={`addProduct.${index}.midLevelCustomerPrice`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="number"
                                    placeholder="0.00"
                                    className="form-control form-control-sm shadow-sm border-dark increased-input"
                                  />
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary">Wholesale Price</label>
                              <Controller
                                name={`addProduct.${index}.lowLevelCustomerPrice`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="number"
                                    placeholder="0.00"
                                    className="form-control form-control-sm shadow-sm border-dark"
                                  />
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary d-block text-truncate">Mfg Date</label>
                              <Controller
                                name={`addProduct.${index}.manufacturingDate`}
                                control={control}
                                render={({ field }) => (
                                  <DatePicker
                                    {...field}
                                    selected={field.value ? new Date(field.value) : null}
                                    onChange={(date) => field.onChange(date)}
                                    placeholderText="YYYY-MM-DD"
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control form-control-sm shadow-sm border-dark w-100 increased-input"
                                  />
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label small fw-bold text-secondary d-block text-truncate">Expiry Date</label>
                              <Controller
                                name={`addProduct.${index}.expiryDate`}
                                control={control}
                                render={({ field }) => (
                                  <DatePicker
                                    {...field}
                                    selected={field.value ? new Date(field.value) : null}
                                    onChange={(date) => field.onChange(date)}
                                    placeholderText="YYYY-MM-DD"
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control form-control-sm shadow-sm border-dark w-100 increased-input"
                                  />
                                )}
                              />
                            </div>
                          </div>

                          {/* Inventory Section */}
                          <div className="row g-3">
                            <div className="col-12 mb-2">
                              <h6 className="text-primary fw-bold border-bottom pb-2">
                                <i className="bi bi-archive me-2"></i>Inventory
                              </h6>
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">
                                Quantity <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name={`addProduct.${index}.quantity`}
                                control={control}
                                rules={{ required: "Required" }}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <input
                                      {...field}
                                      type="number"
                                      placeholder="0"
                                      className={`form-control form-control-sm shadow-sm border-dark increased-input ${error ? 'is-invalid' : ''}`}
                                    />
                                    {error && <div className="invalid-feedback">{error.message}</div>}
                                  </>
                                )}
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">Quantity Type</label>
                              <div className="d-flex align-items-center gap-2">
                                <Controller
                                  name={`addProduct.${index}.quantityType`}
                                  control={control}
                                  render={({ field }) => {
                                    // Convert API data to react-select options format
                                    const qtyOptions = (Array.isArray(quantityTypesData) ? quantityTypesData : []).map(qt => ({
                                      id: qt.id || qt.quantityTypeId,
                                      label: qt.name || qt.quantityTypeName,
                                      value: qt.name || qt.quantityTypeName,
                                      originalData: qt
                                    }));



                                    const selectedOption = qtyOptions.find(opt => opt.value === field.value) || null;

                                    return (
                                      <Select
                                        {...field}
                                        isLoading={quantityTypesLoading}
                                        options={qtyOptions}
                                        value={selectedOption}
                                        onChange={(selected) => field.onChange(selected ? selected.value : '')}
                                        components={{ Option: CustomOption }}
                                        onEditItem={handleEditQuantityType}
                                        onDeleteItem={handleDeleteQuantityType}
                                        placeholder="Select Qty"
                                        classNamePrefix="react-select"
                                        className="flex-grow-1 shadow-sm"
                                        styles={{
                                          control: (provided, state) => ({
                                            ...provided,
                                            minHeight: '31px',
                                            height: '31px',
                                            borderRadius: '4px',
                                            borderColor: state.isFocused ? '#86b7fe' : '#212529',
                                            boxShadow: state.isFocused ? '0 0 0 .25rem rgba(13,110,253,.25)' : 'none',
                                            transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out',
                                            '&:hover': {
                                              borderColor: state.isFocused ? '#86b7fe' : '#212529',
                                            }
                                          }),
                                          valueContainer: (provided) => ({
                                            ...provided,
                                            padding: '0 8px',
                                            height: '31px',
                                          }),
                                          input: (provided) => ({
                                            ...provided,
                                            margin: 0,
                                            padding: 0,
                                          }),
                                          indicatorsContainer: (provided) => ({
                                            ...provided,
                                            height: '31px',
                                          }),
                                          dropdownIndicator: (provided) => ({
                                            ...provided,
                                            padding: '4px',
                                          }),
                                          clearIndicator: (provided) => ({
                                            ...provided,
                                            padding: '4px',
                                          }),
                                          placeholder: (provided) => ({
                                            ...provided,
                                            fontSize: '0.875rem',
                                            margin: 0,
                                          }),
                                          singleValue: (provided) => ({
                                            ...provided,
                                            fontSize: '0.875rem',
                                            margin: 0,
                                          }),
                                          option: (provided, state) => ({
                                            ...provided,
                                            fontSize: '0.875rem',
                                            padding: '8px 12px',
                                          })
                                        }}
                                      />
                                    );
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  data-bs-toggle="modal"
                                  data-bs-target="#createQuantityTypeModal"
                                  title="Add new Quantity Type"
                                  onClick={() => setEditingQuantityType(null)}
                                >
                                  <i className="bi bi-plus-lg"></i>
                                </button>
                                {/* Hidden button to trigger modal programmatically for edit flow */}
                                <button
                                  type="button"
                                  id="hiddenCreateQtyModalBtn"
                                  data-bs-toggle="modal"
                                  data-bs-target="#createQuantityTypeModal"
                                  style={{ display: 'none' }}
                                />
                              </div>
                            </div>



                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label small fw-bold text-secondary">Stock Status</label>
                              <Controller
                                name={`addProduct.${index}.stockStatus`}
                                control={control}
                                render={({ field }) => (
                                  <select
                                    {...field}
                                    className="form-select form-select-sm shadow-sm border-dark"
                                  >
                                    <option value="In Stock">In Stock</option>
                                    <option value="Out Of Stock">Out Of Stock</option>
                                  </select>
                                )}
                              />
                            </div>

                            {state?.id ? null : (
                              <div className="col-12 col-md-6 col-lg-3 d-flex align-items-end">
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm w-100 shadow-sm fw-bold d-flex align-items-center justify-content-center"
                                  onClick={() => append({
                                    registerId: userId || companyDetails?.id || "",
                                    vendorName: "",
                                    vendorAddress: "",
                                    vendorPhone: "",
                                    productName: "",
                                    hsnCode: "",
                                    category: watch("category") || "",
                                    productType: "",
                                    productFor: "Both",
                                    purchasePrice: "",
                                    highLevelCustomerPrice: "",
                                    midLevelCustomerPrice: "",
                                    lowLevelCustomerPrice: "",
                                    manufacturingDate: null,
                                    expiryDate: null,
                                    stockStatus: "In Stock",
                                    quantity: "",
                                    quantityType: "",
                                    otherQuantityType: "",
                                  })}
                                >
                                  <i className="bi bi-plus-lg me-2"></i> Add More Product
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="card-footer bg-white border-top p-3">
                  <div className="row justify-content-end">
                    <div className="col-12 col-md-3">
                      <button
                        type="submit"
                        disabled={postLoading || putLoading}
                        className="btn btn-primary btn-lg w-100 rounded-3 shadow fw-bold d-flex align-items-center justify-content-center py-2"
                      >
                        {postLoading || putLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            {state?.id ? "Update Product" : "Submit"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Styles for DatePicker and custom tweaks */}
      <style>{`
        .react-datepicker-wrapper {
          display: block;
        }
        .form-control:focus, .form-select:focus {
          border-color: #4e73de;
          box-shadow: 0 0 0 0.25rem rgba(78, 115, 222, 0.25);
        }
        .bg-light {
          background-color: #f8f9fc !important;
        }
        .text-primary {
          color: #4e73de !important;
        }
        .btn-primary {
          background-color: #4e73de;
          border-color: #4e73de;
        }
        .btn-primary:hover {
          background-color: #2e59d9;
          border-color: #2e59d9;
        }
        .rounded-4 {
          border-radius: 1rem !important;
        }
        .form-label {
          margin-bottom: 0.25rem;
        }
      `}</style>

      {/* Render the modal component outside the main container/form to avoid nesting issues */}
      <CreateQuantityType editData={editingQuantityType} onClose={() => setEditingQuantityType(null)} />
    </div>
  );
};

export default AddProduct;
